import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES, splitPhone, joinPhone, flagOf } from "@/lib/countries";

// Single-string phone in international format with a searchable country picker in front
export function PhoneInput({ value, onChange, className = "h-9", testid = "phone", placeholder = "Mobile number", warnNoCode = false, autoFocus }) {
  const [open, setOpen] = useState(false);
  const { dial, national, hasCode } = splitPhone(value);
  const country = COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0];
  const emit = (d, n) => onChange(joinPhone(d, n));
  return (
    <div className="space-y-1">
      <div className="flex gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={`${className} shrink-0 inline-flex items-center gap-1 rounded-md border px-2 text-sm bg-white hover:bg-slate-50 ${warnNoCode && !hasCode ? "border-amber-400 bg-amber-50" : "border-input"}`} data-testid={`${testid}-country`} title={country.name}>
              <span className="text-base leading-none">{flagOf(country.iso)}</span>
              <span className="font-mono text-xs">+{country.dial}</span>
              <ChevronDown className="h-3 w-3 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[280px]" align="start">
            <Command>
              <CommandInput placeholder="Search country or code…" data-testid={`${testid}-country-search`} />
              <CommandList className="max-h-64">
                <CommandEmpty>No country found</CommandEmpty>
                <CommandGroup>
                  {COUNTRIES.map((c) => (
                    <CommandItem key={c.iso} value={`${c.name} +${c.dial} ${c.iso}`} onSelect={() => { emit(c.dial, national || ""); if (!national) onChange(`+${c.dial} `); setOpen(false); }} data-testid={`${testid}-country-${c.iso}`}>
                      <span className="mr-2">{flagOf(c.iso)}</span><span className="flex-1 truncate">{c.name}</span>
                      <span className="font-mono text-xs text-slate-500">+{c.dial}</span>
                      {c.dial === dial && <Check className="h-3.5 w-3.5 ml-2 text-brand" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input value={national} onChange={(e) => { const n = e.target.value; n.trim().startsWith("+") ? onChange(n) : emit(dial, n); }} className={className} placeholder={placeholder} inputMode="tel" autoFocus={autoFocus} data-testid={testid} />
      </div>
      {warnNoCode && !hasCode && national && <p className="text-[11px] text-amber-700" data-testid={`${testid}-nocode-hint`}>No country code on the card — confirm the country before saving (India +91 pre-selected).</p>}
    </div>
  );
}
